import { FC, MouseEvent } from 'react';
import { SpaceKind } from 'shared';
import { twMerge } from 'tailwind-merge';

import {
  DotsVerticalIcon,
  EyeIconSmall,
  FolderDownloadIcon,
  SettingsIcon,
  TrashBinIcon,
  UsersIcon,
} from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/popover/index';
import { useLocale } from '../../../../hooks/index';
import { selectedCompanyStorage } from '../../../../storage/company/selected-company.util';
import { AccessRole } from '../../../../types/stakeholder.types';
import {
  getWorkspaceCardMenuItems,
  WorkspaceCardMenuItem,
} from '../../constants/workspace-card-menu.const';
import { WorkspaceCardMenuAction } from '../../types/data-room.types';

const MENU_ICON_COLOR = '#344054';
const MENU_DELETE_ICON_COLOR = '#F04438';

const MENU_ACTION_ICON = {
  [WorkspaceCardMenuAction.DELETE]: TrashBinIcon,
  [WorkspaceCardMenuAction.DOWNLOAD]: FolderDownloadIcon,
  [WorkspaceCardMenuAction.SETTINGS]: SettingsIcon,
  [WorkspaceCardMenuAction.SHARE_ACCESS]: UsersIcon,
  [WorkspaceCardMenuAction.VIEW]: EyeIconSmall,
};

const WRITE_ACTIONS: ReadonlySet<WorkspaceCardMenuAction> = new Set([
  WorkspaceCardMenuAction.DELETE,
  WorkspaceCardMenuAction.SETTINGS,
]);

export type WorkspaceCardMenuProps = {
  /**
   * Given when the list is not the owner's. A recipient's card decides it from the permission on
   * their own grant, which the role in the selected company says nothing about.
   */
  items?: WorkspaceCardMenuItem[];
  kind: SpaceKind;
  onAction?: (action: WorkspaceCardMenuAction) => void;
};

export const WorkspaceCardMenu: FC<WorkspaceCardMenuProps> = ({ items: given, kind, onAction }) => {
  const { messagesLocale } = useLocale();
  const isAuditor = selectedCompanyStorage(
    ({ company }) => company?.stakeholder?.accessRole === AccessRole.AUDITOR,
  );
  const items = given ?? getWorkspaceCardMenuItems(kind, { isAuditor });

  if (items.length === 0) {
    return null;
  }

  const stopCardClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <Popover>
      <PopoverTrigger
        aria-label={messagesLocale['MORE_ACTIONS']}
        className="flex size-4 items-center justify-center"
        onClick={stopCardClick}
      >
        <DotsVerticalIcon className="size-4" iconColor={MENU_ICON_COLOR} />
      </PopoverTrigger>
      <PopoverContent
        className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal"
        onClick={stopCardClick}
      >
        {items.map((item) => {
          const Icon = MENU_ACTION_ICON[item.action];

          return (
            <PopoverClose
              className={twMerge(
                'flex h-9 w-full items-center gap-2 rounded px-[6px] pr-8 text-sm hover:bg-gray-50',
                item.isDestructive ? 'text-fireside-500' : 'text-gray-700',
              )}
              data-permission-required={WRITE_ACTIONS.has(item.action) || undefined}
              key={item.action}
              onClick={() => onAction?.(item.action)}
            >
              <Icon
                className="size-4"
                iconColor={item.isDestructive ? MENU_DELETE_ICON_COLOR : MENU_ICON_COLOR}
              />
              <span
                className={twMerge(
                  'text-sm font-normal',
                  item.isDestructive ? 'text-fireside-500' : 'text-gray-700',
                )}
              >
                <AppFormattedMessage id={item.label} />
              </span>
            </PopoverClose>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};
